import { BasketballCoachLog } from '@hefe/models';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import API from '../../../api/api';
import { BasketballCoachLoggingCreateRequest } from '../../../models/requests/basketball-coach-logging';

const FEATURE_KEY = 'basketballCoachLogging';

/************************************************************************************
 *
 *                                  ACTIONS
 *
 ************************************************************************************
 */
export const fetchBasketballCoachLogging = createAsyncThunk(
  'basketballCoachLogging/getAll',
  async (thunkAPI) => {
    const response = await API.basketballCoachPresences().getAll();
    return response?.data;
  }
);

export const createBasketballCoachLogging = createAsyncThunk(
  'basketballCoachLogging/create',
  async (data: BasketballCoachLoggingCreateRequest, thunkAPI) => {
    const response = await API.basketballCoachPresences().save(data);
    return response?.data;
  }
);

export const deleteBasketballCoachLogging = createAsyncThunk<
  any,
  BasketballCoachLog
>(
  'basketballCoachLogging/delete',
  async (data: BasketballCoachLog, thunkAPI) => {
    const response = await API.basketballCoachPresences().delete(data);
    if (response) {
      return thunkAPI.fulfillWithValue(data);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

export interface BasketballCoachLoggingState {
  logs: BasketballCoachLog[];
}

const initialState: BasketballCoachLoggingState = {
  logs: [],
};

export const basketballCoachLoggingSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      fetchBasketballCoachLogging.fulfilled,
      (
        state: BasketballCoachLoggingState,
        action: PayloadAction<BasketballCoachLog[]>
      ) => {
        state.logs = action.payload;
      }
    );
    builder.addCase(
      createBasketballCoachLogging.fulfilled,
      (
        state: BasketballCoachLoggingState,
        action: PayloadAction<BasketballCoachLog>
      ) => {
        const index = state.logs.findIndex(log => log.id === action.payload.id);
        if (index > -1) {
          state.logs[index] = action.payload;
        } else {
          state.logs.push(action.payload);
        }
        // Sort by date
        state.logs.sort(function (a, b) {
          return dayjs(a.date).millisecond() - dayjs(b.date).millisecond();
        });
      }
    );
    builder.addCase(
      deleteBasketballCoachLogging.fulfilled,
      (
        state: BasketballCoachLoggingState,
        action: PayloadAction<BasketballCoachLog>
      ) => {
        state.logs = state.logs.filter((log) => log.id !== action.payload.id);
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
export const basketballCoachLoggingReducer =
  basketballCoachLoggingSlice.reducer;
export const basketballCoachLoggingActions =
  basketballCoachLoggingSlice.actions;

const state = (rootState: any): BasketballCoachLoggingState =>
  rootState[FEATURE_KEY];

export const basketballCoachLogsSelector = createSelector(state, (state) => {
  return state.logs;
});
